import { useSelector } from 'react-redux'
import { CharacterCountIndicator, Text } from 'common-components'
import { dispatch } from 'store'
import { aiVideoThunks } from 'store/features/aiVideo/thunks'
import { aiVideoSelectors } from 'store/features/aiVideo/selectors'
import { projectDataSelectors } from 'store/features/projectData/selectors'
import {
  IMentionPickerOption,
  PromptEditor
} from 'containers/SubSidebar/shared/PromptMentionEditor'
import * as Styled from './styled'
import {
  PROMPT_MAX_HEIGHT,
  PROMPT_MAX_HEIGHT_MOBILE,
  PROMPT_MIN_HEIGHT
} from './consts'
import { translate } from 'utils/translations'
import { T } from 'consts/translationKeys'
import { memo, useMemo, useState } from 'react'
import { isBlobUrl } from 'utils'

const TextAreaSection = () => {
  const value = useSelector(aiVideoSelectors.prompt)
  const modelPromptConfig = useSelector(
    aiVideoSelectors.selectedModelPromptConfig
  )
  const budgetedLength = useSelector(aiVideoSelectors.promptBudgetedLength)
  const hasMediaItems = useSelector(projectDataSelectors.hasMediaItems)
  const refImages = useSelector(aiVideoSelectors.multiRefImagesBucket)
  const isMultiRefSupported = useSelector(aiVideoSelectors.isMultiRefSupported)
  const isMultiRefMode = useSelector(aiVideoSelectors.isMultiRefMode)
  const [isFocused, setIsFocused] = useState(false)

  const placeholderTexts = useMemo(
    () => [
      translate(T.VIDEO_PROMPT_PLACEHOLDER_ANIMATE),
      translate(T.VIDEO_PROMPT_PLACEHOLDER_COMBINE),
      translate(T.VIDEO_PROMPT_PLACEHOLDER_MOTION)
    ],
    []
  )

  /**
   * Only the multi-reference IMAGES bucket is mentionable. An empty array makes
   * `@` inert, which covers both the Start/End frame bucket (whose order is not
   * stable — addReferenceMediaItem re-pushes) and models without multi-reference
   * support. The store strips existing tokens on those transitions, so the
   * prompt cannot keep a chip the picker no longer offers.
   *
   * An image still uploading cannot be referenced, so it is omitted rather than
   * shown as an unusable row — and `imageNumber` stays its index in the FULL
   * bucket, so the skipped position stays RESERVED (3 images, #2 uploading ->
   * "Image 1", "Image 3"). The gap is deliberate: re-indexing the survivors to
   * 1..k would silently repoint existing chips the moment an upload finished.
   */
  const mentionOptions = useMemo<Array<IMentionPickerOption>>(() => {
    if (!isMultiRefSupported || !isMultiRefMode) {
      return []
    }

    return refImages.flatMap((image, index) =>
      image.isLoading || !image.url || isBlobUrl(image.url)
        ? []
        : [{ id: image.id, imageNumber: index + 1, thumbnailUrl: image.url }]
    )
  }, [refImages, isMultiRefSupported, isMultiRefMode])

  // The prompt goes to the store on every keystroke (no debounce): a pending
  // timer could otherwise land AFTER a store-side mention renumber and
  // resurrect stale chip numbers.
  const handleChange = (newValue: string) => {
    dispatch(aiVideoThunks.setPrompt(newValue))
  }

  const maxChars = modelPromptConfig.isUnlimited
    ? undefined
    : modelPromptConfig.maxCharacters

  // Measured against the BUDGETED length (chips expand in the request), the same
  // figure the Generate gate uses — so the counter and the red outline always
  // explain a disabled Generate instead of reading "in bounds" while it is off.
  const isAtLimit =
    !!budgetedLength && maxChars !== undefined && budgetedLength >= maxChars

  // Placeholder gating is unchanged from the textarea it replaces: the typewriter
  // only on a pristine, unfocused prompt in an empty project, otherwise the
  // static hint (and nothing at all while focused). A contenteditable has no
  // `placeholder` attribute, so both render as an overlay.
  const isPlaceholderActive = !hasMediaItems && !isFocused

  return (
    <Styled.TextAreaSection>
      <Text type="h2" fontWeight="600" color="secondaryDark">
        {translate(T.PROMPT)}
      </Text>
      <div>
        <PromptEditor
          value={value ?? ''}
          onChange={handleChange}
          placeholderTexts={placeholderTexts}
          isPlaceholderActive={isPlaceholderActive}
          staticPlaceholder={isFocused ? '' : translate(T.PROMPT_PLACEHOLDER)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          minHeight={PROMPT_MIN_HEIGHT}
          maxHeight={PROMPT_MAX_HEIGHT}
          maxHeightMobile={PROMPT_MAX_HEIGHT_MOBILE}
          maxLength={maxChars}
          hasError={isAtLimit}
          options={mentionOptions}
          data-testid="ai-video-prompt-editor"
        />
        {maxChars !== undefined && (
          <CharacterCountIndicator
            currentTextLength={budgetedLength}
            maxCount={maxChars}
            textWarningState={isAtLimit ? 'error' : undefined}
          />
        )}
      </div>
    </Styled.TextAreaSection>
  )
}

export default memo(TextAreaSection)
